import { useState } from "react";
import { useQueryClient } from "react-query";
import { MenuItem, TextField } from "@mui/material";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import printService, { printQueryKeys } from "api/printService";
import { IError } from "dto/IError";
import {
  EPrintDirection,
  EPrintFormatCategory,
  EPrintReference,
  EPrintRotation,
  IPrintFormat,
  IPrintProfile,
  ISavePrintFormat,
  ISavePrintProfile,
} from "dto/Printing/IOutgoingPrint";
import { ENotificationType, notification } from "utils/notification";

import {
  FormatForm,
  FormatNumberKey,
  ProfileForm,
  fromFormatForm,
  fromProfileForm,
  parseNumber,
  toFormatForm,
  toProfileForm,
} from "./printSettingsForm";
import styles from "./styles.module.scss";

// Formaty i profile wydruku firmy — port edytorów Magistratu (PrintSettingsEditorVM:
// PrintFormatMetricVM „Nazwa / Rozmiar", PrintProfileMetricVM „Wyrównanie / Przesunięcie /
// Kierunek / Obrót", DefaultPrintSettingsEditorVM „format domyślny"). Wymiary w milimetrach.
// Wybór formatu, papieru i opcji przy konkretnym wydruku pamięta każde stanowisko osobno.

const errorText = (e: unknown, fallback: string) =>
  (e as IError)?.response?.data?.errorCodes?.[0]?.message ?? fallback;

const num = (v?: number | null) =>
  v == null ? "" : Number(v).toLocaleString("pl-PL", { maximumFractionDigits: 2 });

const CATEGORY_TITLES: Record<number, { title: string; hint: string }> = {
  [EPrintFormatCategory.AddressSticker]: {
    title: "Naklejki adresowe",
    hint: "Rozmiar pojedynczej naklejki. Dla arkuszy A4 możesz podać układ (kolumny, wiersze, marginesy, odstępy) — puste pola wylicza program, rozkładając naklejki równo na arkuszu.",
  },
  [EPrintFormatCategory.Envelope]: {
    title: "Koperty",
    hint: "Rozmiar koperty (szerokość × wysokość). Adresat drukowany jest w prawej dolnej ćwiartce, nadawca w lewym górnym rogu.",
  },
};

// Etykiety jak w PrintProfileMetricControl Magistratu.
const REF_X = [
  { value: EPrintReference.LowerBound, label: "Do lewej" },
  { value: EPrintReference.Center, label: "Do środka" },
  { value: EPrintReference.UpperBound, label: "Do prawej" },
];
const REF_Y = [
  { value: EPrintReference.UpperBound, label: "Do góry" },
  { value: EPrintReference.Center, label: "Do środka" },
  { value: EPrintReference.LowerBound, label: "Do dołu" },
];
const DIR_X = [
  { value: EPrintDirection.Ascending, label: "W prawo" },
  { value: EPrintDirection.Descending, label: "W lewo" },
];
const DIR_Y = [
  { value: EPrintDirection.Descending, label: "W dół" },
  { value: EPrintDirection.Ascending, label: "W górę" },
];
const ROTATIONS = [
  { value: EPrintRotation.None, label: "Brak" },
  { value: EPrintRotation.Negative, label: "W prawo (90°)" },
  { value: EPrintRotation.Positive, label: "W lewo (90°)" },
  { value: EPrintRotation.Invert, label: "Odwrócenie (180°)" },
];

const labelOf = (list: { value: number; label: string }[], v: number) =>
  list.find((x) => x.value === v)?.label ?? "";

const profileSummary = (p: IPrintProfile) =>
  [
    `poziomo: ${labelOf(REF_X, p.ReferenceX).toLowerCase()}${p.OffsetXMm ? `, ${num(p.OffsetXMm)} mm ${labelOf(DIR_X, p.DirectionX).toLowerCase()}` : ""}`,
    `pionowo: ${labelOf(REF_Y, p.ReferenceY).toLowerCase()}${p.OffsetYMm ? `, ${num(p.OffsetYMm)} mm ${labelOf(DIR_Y, p.DirectionY).toLowerCase()}` : ""}`,
    `obrót: ${labelOf(ROTATIONS, p.Rotation).toLowerCase()}`,
  ].join(" · ");

const sheetSummary = (f: IPrintFormat) =>
  f.SheetColumns || f.SheetRows
    ? `arkusz ${f.SheetColumns ?? "auto"} × ${f.SheetRows ?? "auto"}`
    : "układ arkusza automatyczny";

const EMPTY_PROFILE: ISavePrintProfile = {
  Id: null,
  Name: "",
  ReferenceX: EPrintReference.Center,
  ReferenceY: EPrintReference.UpperBound,
  DirectionX: EPrintDirection.Ascending,
  DirectionY: EPrintDirection.Descending,
  OffsetXMm: 0,
  OffsetYMm: 0,
  Rotation: EPrintRotation.None,
};

const newFormat = (category: EPrintFormatCategory): ISavePrintFormat => ({
  Id: null,
  Category: category,
  Name: "",
  WidthMm: category === EPrintFormatCategory.Envelope ? 220 : 70,
  HeightMm: category === EPrintFormatCategory.Envelope ? 110 : 37,
  IsDefault: false,
});

const PrintSettingsTab = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = printService.usePrintSettings();
  const { mutate: saveFormat, isLoading: savingFormat } = printService.useSaveFormat();
  const { mutate: removeFormat } = printService.useRemoveFormat();
  const { mutate: saveProfile, isLoading: savingProfile } = printService.useSaveProfile();
  const { mutate: removeProfile } = printService.useRemoveProfile();

  const [formatDraft, setFormatDraft] = useState<FormatForm | null>(null);
  const [profileDraft, setProfileDraft] = useState<ProfileForm | null>(null);

  const formats = data?.data?.Formats ?? [];
  const profiles = data?.data?.Profiles ?? [];
  const canEdit = data?.data?.CanEdit ?? false;

  const refresh = () => queryClient.invalidateQueries(printQueryKeys.settings);

  const fail = (title: string) => (e: unknown) =>
    notification({ type: ENotificationType.ERROR, title, text: errorText(e, "Spróbuj ponownie.") });

  const submitFormat = (draft: ISavePrintFormat) =>
    saveFormat(draft, {
      onSuccess: () => {
        setFormatDraft(null);
        refresh();
      },
      onError: fail("Nie zapisano formatu"),
    });

  const setDefault = (f: IPrintFormat) => submitFormat({ ...f, IsDefault: true });

  const deleteFormat = (f: IPrintFormat) => {
    if (!window.confirm(`Usunąć format „${f.Name}”?`)) return;
    removeFormat(f.Id, { onSuccess: refresh, onError: fail("Nie usunięto formatu") });
  };

  const submitProfile = (draft: ISavePrintProfile) =>
    saveProfile(draft, {
      onSuccess: () => {
        setProfileDraft(null);
        refresh();
      },
      onError: fail("Nie zapisano profilu"),
    });

  const deleteProfile = (p: IPrintProfile) => {
    if (!window.confirm(`Usunąć profil „${p.Name}”?`)) return;
    removeProfile(p.Id, { onSuccess: refresh, onError: fail("Nie usunięto profilu") });
  };

  const formatEditor = (draft: FormatForm) => {
    const sticker = draft.Category === EPrintFormatCategory.AddressSticker;
    const set = (patch: Partial<FormatForm>) => setFormatDraft({ ...draft, ...patch });
    const numberField = (label: string, key: FormatNumberKey, placeholder?: string) => (
      <TextField
        size="small"
        label={label}
        placeholder={placeholder}
        value={draft[key]}
        error={parseNumber(draft[key]) === undefined}
        inputProps={{ inputMode: "decimal" }}
        onChange={(e) => set({ [key]: e.target.value } as Partial<FormatForm>)}
      />
    );
    const valid = fromFormatForm(draft);
    return (
      <div className={styles.editor}>
        <div className={styles.editorTitle}>{draft.Id ? "Edycja formatu" : "Nowy format"}</div>
        <TextField size="small" label="Nazwa" value={draft.Name} onChange={(e) => set({ Name: e.target.value })} />
        <div className={styles.grid2}>
          {numberField("Szerokość (mm)", "WidthMm")}
          {numberField("Wysokość (mm)", "HeightMm")}
        </div>
        {sticker && (
          <>
            <div className={styles.subTitle}>Arkusz A4 (opcjonalnie)</div>
            <div className={styles.grid2}>
              {numberField("Kolumny", "SheetColumns", "auto")}
              {numberField("Wiersze", "SheetRows", "auto")}
              {numberField("Margines górny (mm)", "SheetMarginTopMm", "auto")}
              {numberField("Margines lewy (mm)", "SheetMarginLeftMm", "auto")}
              {numberField("Odstęp poziomy (mm)", "SheetGapXMm", "auto")}
              {numberField("Odstęp pionowy (mm)", "SheetGapYMm", "auto")}
            </div>
          </>
        )}
        <label className={styles.check}>
          <input type="checkbox" checked={draft.IsDefault} onChange={(e) => set({ IsDefault: e.target.checked })} />
          Format domyślny
        </label>
        <div className={styles.editorActions}>
          <HollowButton text="Anuluj" onClick={() => setFormatDraft(null)} />
          <Button
            text="Zapisz"
            onClick={() => valid && submitFormat(valid)}
            loading={savingFormat}
            disabled={!draft.Name.trim() || !valid}
          />
        </div>
      </div>
    );
  };

  const profileEditor = (draft: ProfileForm) => {
    const set = (patch: Partial<ProfileForm>) => setProfileDraft({ ...draft, ...patch });
    const valid = fromProfileForm(draft);
    const offsetField = (key: "OffsetXMm" | "OffsetYMm") => (
      <TextField
        size="small"
        label="Przesunięcie (mm)"
        value={draft[key]}
        error={parseNumber(draft[key]) === undefined}
        inputProps={{ inputMode: "decimal" }}
        onChange={(e) => set({ [key]: e.target.value } as Partial<ProfileForm>)}
      />
    );
    const select = (label: string, value: number, list: { value: number; label: string }[], key: keyof ProfileForm) => (
      <TextField
        select
        size="small"
        label={label}
        value={value}
        onChange={(e) => set({ [key]: Number(e.target.value) } as Partial<ProfileForm>)}
      >
        {list.map((x) => (
          <MenuItem key={x.value} value={x.value}>
            {x.label}
          </MenuItem>
        ))}
      </TextField>
    );
    return (
      <div className={styles.editor}>
        <div className={styles.editorTitle}>{draft.Id ? "Edycja profilu" : "Nowy profil"}</div>
        <TextField size="small" label="Nazwa" value={draft.Name} onChange={(e) => set({ Name: e.target.value })} />
        <div className={styles.subTitle}>W poziomie</div>
        <div className={styles.grid3}>
          {select("Wyrównanie", draft.ReferenceX, REF_X, "ReferenceX")}
          {offsetField("OffsetXMm")}
          {select("Kierunek przesunięcia", draft.DirectionX, DIR_X, "DirectionX")}
        </div>
        <div className={styles.subTitle}>W pionie</div>
        <div className={styles.grid3}>
          {select("Wyrównanie", draft.ReferenceY, REF_Y, "ReferenceY")}
          {offsetField("OffsetYMm")}
          {select("Kierunek przesunięcia", draft.DirectionY, DIR_Y, "DirectionY")}
        </div>
        {select("Obrót", draft.Rotation, ROTATIONS, "Rotation")}
        <div className={styles.editorActions}>
          <HollowButton text="Anuluj" onClick={() => setProfileDraft(null)} />
          <Button
            text="Zapisz"
            onClick={() => valid && submitProfile(valid)}
            loading={savingProfile}
            disabled={!draft.Name.trim() || !valid}
          />
        </div>
      </div>
    );
  };

  const formatSection = (category: EPrintFormatCategory) => {
    const list = formats.filter((f) => f.Category === category);
    const meta = CATEGORY_TITLES[category];
    const editing = formatDraft && formatDraft.Category === category ? formatDraft : null;
    return (
      <div className={styles.section} key={category}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>{meta.title}</div>
          {canEdit && !editing && (
            <HollowButton text="Dodaj format" onClick={() => setFormatDraft(toFormatForm(newFormat(category)))} />
          )}
        </div>
        <div className={styles.hint}>{meta.hint}</div>
        {editing && !editing.Id && formatEditor(editing)}
        <div className={styles.list}>
          {list.length === 0 && <div className={styles.empty}>Brak formatów.</div>}
          {list.map((f) =>
            editing?.Id === f.Id ? (
              <div key={f.Id}>{formatEditor(editing)}</div>
            ) : (
              <div key={f.Id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>
                    {f.Name}
                    {f.IsDefault && <span className={styles.badge}>domyślny</span>}
                  </div>
                  <div className={styles.rowMeta}>
                    {num(f.WidthMm)} × {num(f.HeightMm)} mm
                    {category === EPrintFormatCategory.AddressSticker ? ` · ${sheetSummary(f)}` : ""}
                  </div>
                </div>
                {canEdit && (
                  <div className={styles.rowActions}>
                    {!f.IsDefault && <HollowButton text="Ustaw domyślny" onClick={() => setDefault(f)} />}
                    <HollowButton text="Edytuj" onClick={() => setFormatDraft(toFormatForm(f))} />
                    <HollowButton text="Usuń" onClick={() => deleteFormat(f)} />
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  if (isLoading) return <div className={styles.wrapper}>Ładowanie…</div>;

  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Formaty wydruku</div>
        <div className={styles.hint}>
          Wspólne dla całej firmy formaty naklejek adresowych i kopert oraz profile wydruku używane przy
          drukowaniu przesyłek wychodzących (Projekty pism, Wychodzące pisma, biuro podawcze).
          {!canEdit && " Zmieniać je może właściciel firmy, administrator albo biuro podawcze."}
        </div>
      </div>

      {formatSection(EPrintFormatCategory.AddressSticker)}
      {formatSection(EPrintFormatCategory.Envelope)}

      <div className={styles.section}>
        <div className={styles.sectionHead}>
          <div className={styles.sectionTitle}>Profile wydruku</div>
          {canEdit && !profileDraft && (
            <HollowButton text="Dodaj profil" onClick={() => setProfileDraft(toProfileForm(EMPTY_PROFILE))} />
          )}
        </div>
        <div className={styles.hint}>
          Profil opisuje, jak wydruk (naklejka, arkusz, koperta, zwrotka ZPO) ma leżeć na papierze
          w drukarce: wyrównanie, przesunięcie i obrót — do kalibracji podajników kopert i kart.
          Wybiera się go w oknie drukowania razem z rozmiarem papieru.
        </div>
        {profileDraft && !profileDraft.Id && profileEditor(profileDraft)}
        <div className={styles.list}>
          {profiles.length === 0 && <div className={styles.empty}>Brak profili — wydruk używa ułożenia domyślnego.</div>}
          {profiles.map((p) =>
            profileDraft?.Id === p.Id ? (
              <div key={p.Id}>{profileEditor(profileDraft)}</div>
            ) : (
              <div key={p.Id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>{p.Name}</div>
                  <div className={styles.rowMeta}>{profileSummary(p)}</div>
                </div>
                {canEdit && (
                  <div className={styles.rowActions}>
                    <HollowButton text="Edytuj" onClick={() => setProfileDraft(toProfileForm(p))} />
                    <HollowButton text="Usuń" onClick={() => deleteProfile(p)} />
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintSettingsTab;
